import { CropPurpose, CropStyleFillPattern } from "@omnia/shared/generated/enums";
import { createLookup, FieldViewModel, getDistinctValues, groupBy, ICrop, IOmniaSession, param } from "../omnia";
import { StylesApi } from "./api";
import { getMasterStyles } from "./defaults";
import {
    ApiStyle,
    BasicStyle,
    CropStyle,
    DescriptionStyle,
    GrassStyle,
    PurposeStyle,
    State,
    VarietyStyle,
} from "./types";

export function createInitialState(
    session: IOmniaSession,
    refresh: () => PromiseLike<any>,
    { cropping, crops }: { cropping: FieldViewModel[]; crops: ICrop[] }
): ng.IPromise<State> {
    const api = new StylesApi(session, refresh);

    const { holding, yearId: currentYear } = session;

    const availableYears = holding.CropYears.map((y) => y.YearId);

    const masterStyles = getMasterStyles(crops);

    const state: State = {
        api,

        currentYear,
        availableYears,
        initialYears: [currentYear],
        selectedYears: [currentYear],

        cropping,
        masterStyles,
        crops: getCropStyles(cropping, masterStyles),
    };

    const nextState = api.getStyles().then((styles) => mergeStyles(state, styles));

    return nextState;
}

export function refreshState(state: State) {
    const { api, cropping, masterStyles } = state;

    state = {
        ...state,
        crops: getCropStyles(cropping, masterStyles),
    };

    return api.getStyles().then((styles) => mergeStyles(state, styles));
}

/**
 * Merges styles read from the API into the available crop groups
 * @param prev Current state
 * @param styles Styles read from the API
 * @returns Updated state, with styles merged into the crop groups
 */
function mergeStyles(prev: State, styles: ApiStyle[]): State {
    const stylesForYear = groupApiStylesByYear(styles, prev.currentYear);

    const cropMap = new Map(stylesForYear.map((c) => [c.cropId, c]));

    const crops = prev.crops.map((c) => mergeCropApiStyle(c, cropMap.get(c.cropId)));

    return {
        ...prev,
        crops,
    };
}

/**
 * Merges a style provided by the API into a crop level local style
 *
 * @param target Local crop style
 * @param source API style
 * @returns Resulting crop style
 */
function mergeCropApiStyle(target: CropStyle, source: ReturnType<typeof groupApiStylesByYear>[0]) {
    if (!source) {
        return target;
    }

    const varieties = mergeByKey(
        target.varieties,
        source.varieties,
        (a, b) => a.varietyId === b.varietyId,
        (a, b) => {
            const initialState: VarietyStyle["initialState"] = {
                color: b?.color ?? null,
                pattern: b?.pattern ?? null,
            };
            return {
                ...a,
                ...initialState,
                initialState,
            };
        }
    );

    const purposes = mergeByKey(
        target.purposes,
        source.purposes,
        (a, b) => a.purpose === b.purpose,
        (a, b) => {
            const initialState: PurposeStyle["initialState"] = {
                color: b?.color ?? null,
                pattern: b?.pattern ?? null,
            };
            return {
                ...a,
                ...initialState,
                initialState,
            };
        }
    );

    const grasses = mergeByKey(
        target.grasses,
        source.grasses,
        (a, b) => a.grassManagement === b.grassManagement && a.grassCuts === b.grassCuts,
        (a, b) => {
            const initialState: GrassStyle["initialState"] = {
                color: b?.color ?? null,
                pattern: b?.pattern ?? null,
            };
            return {
                ...a,
                ...initialState,
                initialState,
            };
        }
    );

    const descriptions = mergeByKey(
        target.descriptions,
        source.descriptions,
        (a, b) => a.description === b.croppingCropDescr,
        (a, b) => {
            const initialState: DescriptionStyle["initialState"] = {
                color: b?.color ?? null,
                pattern: b?.pattern ?? null,
            };
            return {
                ...a,
                ...initialState,
                initialState,
            };
        }
    );

    const initialState: CropStyle["initialState"] = {
        color: source.color,
        pattern: source.pattern,
    };

    return {
        ...target,
        ...initialState,
        initialState,
        varieties,
        purposes,
        grasses,
        descriptions,
    };
}

function mergeByKey<A, B, C = A>(a: A[], b: B[], match: (a: A, b: B) => boolean, mergeFn: (a: A, b: B) => C) {
    return a.map((a) =>
        mergeFn(
            a,
            b.find((b) => match(a, b))
        )
    );
}

function getCropStyles(croppings: FieldViewModel[], defaultStyles: ReturnType<typeof getMasterStyles>): CropStyle[] {
    const currentYearCroppings = croppings.filter((c) => c.currentYear);

    const croppingsByCrop = groupBy(
        currentYearCroppings,
        (c) => c.crop,
        (key) => key.CropId
    ).filter((group) => Boolean(group.key?.CropId));

    const styles: CropStyle[] = croppingsByCrop
        .map(({ key: crop, values: croppings }) => ({
            ...getCropStyle(crop.CropId, crop.Name, defaultStyles.getCrop(crop.CropId)),
            varieties: getVarietyStyles(croppings, defaultStyles),
            purposes: getPurposeStyles(crop, croppings),
            grasses: getGrassStyles(croppings),
            descriptions: getDescriptionStyles(croppings),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return styles;
}

function getCropStyle(id: number, name: string, defaultStyle: BasicStyle): CropStyle {
    const initialState: CropStyle["initialState"] = {
        color: null,
        pattern: null,
    };

    return {
        // The crop style.
        cropId: id,
        name: name || `Crop #${id}`,
        ...initialState,
        initialState,
        defaultStyle: {
            color: defaultStyle.color,
            pattern: defaultStyle.pattern ?? defaultStyle.color ? CropStyleFillPattern.None : null,
        },

        // The other, nested, styles.
        varieties: [],
        purposes: [],
        grasses: [],
        descriptions: [],
    };
}

function getVarietyStyles(
    croppings: FieldViewModel[],
    defaultStyles: ReturnType<typeof getMasterStyles>
): VarietyStyle[] {
    const croppingsByVariety = groupBy(
        croppings.filter((x) => x.variety),
        (x) => x.variety,
        (key) => key.CropVarietyId
    );

    const varieties: VarietyStyle[] = croppingsByVariety
        .map(({ key: variety, values: croppings }) => ({
            ...getVarietyStyle(variety.CropVarietyId, variety.Name, defaultStyles.getVariety(variety.CropVarietyId)),
            years: [...new Set(croppings.map((x) => x.yearId))],
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return varieties;
}

function getVarietyStyle(id: number, name: string, defaultStyle: BasicStyle): VarietyStyle {
    const initialState: VarietyStyle["initialState"] = {
        color: null,
        pattern: null,
    };

    return {
        varietyId: id,
        name: name || `CropVariety #${id}`,
        ...initialState,
        initialState,
        defaultStyle,
    };
}

/** Set of possible CropPurpose values */
const enumPurposes = new Set(
    param
        .CropPurpose()
        .map((x) => x.value)
        .filter(Boolean)
);

function getPurposeStyles(crop: ICrop, croppings: FieldViewModel[]): PurposeStyle[] {
    // Crop/CropVariety static data can include purposes that no longer exist, or purposes that are specified on the variety whilst no longer valid for the crop.
    // We derive the "valid" purposes as those defined on the crop, that also exist in the static set of possible enum values.
    const validPurposesForCrop = new Set(crop.Purposes?.Purposes?.filter((p) => enumPurposes.has(p)) ?? []);

    const uniquePurposesInUse = new Set(
        croppings
            .flatMap((x) => [x.marketId as CropPurpose, x.variety?.Market])
            .filter((p) => validPurposesForCrop.has(p))
    );

    const initialState: PurposeStyle["initialState"] = {
        color: null,
        pattern: null,
    };

    const styles: PurposeStyle[] = [...uniquePurposesInUse]
        .map((purpose) => ({
            purpose,
            ...initialState,
            initialState,
            defaultStyle: {
                // We do not currently store default styles for CropPurpose.
                color: null,
                pattern: null,
            },
        }))
        .sort((a, b) => a.purpose - b.purpose);

    return styles;
}

function getDescriptionStyles(croppings: FieldViewModel[]): DescriptionStyle[] {
    const uniqueDescriptions = new Set(croppings.map((field) => field.cropDescription).filter(Boolean));

    const initialState: DescriptionStyle["initialState"] = {
        color: null,
        pattern: null,
    };

    const styles: DescriptionStyle[] = [...uniqueDescriptions]
        .map((d) => ({
            description: d,
            ...initialState,
            initialState,
            defaultStyle: {
                // We do not currently store default styles for cropping description.
                color: null,
                pattern: null,
            },
        }))
        .sort((a, b) => a.description.localeCompare(b.description));

    return styles;
}

function getGrassStyles(croppings: FieldViewModel[]): GrassStyle[] {
    const uniqueGrassCombos = getDistinctValues(
        croppings
            .filter((x) => x.crop.Grass)
            .map((x) => ({
                grassMangagement: x.cropping.GrassManagement || null,
                grassCuts: x.cropping.GrassCuts || null,
            })),
        (x) => x,
        (x) => JSON.stringify(x, null, null)
    );

    const initialState: GrassStyle["initialState"] = {
        color: null,
        pattern: null,
    };

    const styles: GrassStyle[] = [...uniqueGrassCombos]
        .map((x) => ({
            grassCuts: x.grassCuts,
            grassManagement: x.grassMangagement,
            ...initialState,
            initialState,
            defaultStyle: {
                // We do not currently store default styles for a [GrassManagement, GrassCuts] combo.
                color: null,
                pattern: null,
            },
        }))
        .sort((a, b) => {
            let value = a.grassManagement - b.grassManagement;
            if (value !== 0) {
                return value;
            } else {
                return a.grassCuts - b.grassCuts;
            }
        });

    return styles;
}

function groupApiStylesByYear(styles: ApiStyle[], year: number) {
    // Get styles for the specified year.
    const stylesForYear = styles.filter((x) => x.year === year);

    // Get all referenced crop IDs
    const cropIds = new Set(stylesForYear.map((x) => x.cropId));

    // Get styles for VARIETIES for the specified year, grouped by crop ID.
    const varietyLookup = createLookup(
        stylesForYear.filter((x) => !!x.varietyId),
        (x) => x.cropId
    );

    // Get styles for PURPOSES for the specified year, grouped by crop ID.
    const purposeLookup = createLookup(
        stylesForYear.filter((x) => !!x.purpose),
        (x) => x.cropId
    );

    // Get styles for GRASSES for the specified year, grouped by crop ID.
    const grassLookup = createLookup(
        stylesForYear.filter((x) => !!x.grassManagement || !!x.grassCuts),
        (x) => x.cropId
    );

    // Get styles for DESCRIPTIONS for the specified year, grouped by crop ID.
    const descriptionLookup = createLookup(
        stylesForYear.filter((x) => !!x.croppingCropDescr),
        (x) => x.cropId
    );

    // Get crop-only styles for the specified year.
    const cropStylesForYear = stylesForYear.filter(
        (x) => !x.varietyId && !x.purpose && !x.croppingCropDescr && !x.grassCuts && !x.grassManagement
    );

    // TODO
    const years = [year];

    return [...cropIds].map((cropId) => {
        const cropStyle = cropStylesForYear.find((x) => x.cropId === cropId);
        return {
            cropId,
            year,
            color: cropStyle?.color ?? null,
            pattern: cropStyle?.pattern ?? null,
            years,
            varieties: varietyLookup[cropId] ?? [],
            purposes: purposeLookup[cropId] ?? [],
            grasses: grassLookup[cropId] ?? [],
            descriptions: descriptionLookup[cropId] ?? [],
        };
    });
}
